'use client';

import { useState, useEffect } from 'react';
import Breadcrumb from '@/components/ui/Breadcrumb';
import NavBar from '../home-page/components/NavBar';
import FooterSection from '../home-page/components/FooterSection';
import { generateBreadcrumbSchema } from '@/lib/utils/metadata';
import { teamService, type TeamMember } from '@/lib/services/teamService';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

export default function TeamPage() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTeam = async () => {
      try {
        const data = await teamService.getActive();
        setTeamMembers(data);
      } catch (error) {
        console.error('Failed to load team:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTeam();
  }, []);

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: `${SITE_URL}/home-page` },
    { name: 'Team', url: `${SITE_URL}/team` },
  ]);

  const personSchemas = teamMembers.map((member) => ({
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: member.fullName,
    jobTitle: member.jobTitle,
    worksFor: {
      '@type': 'Organization',
      name: 'GoGedit',
    },
    ...(member.linkedinUrl && { sameAs: member.linkedinUrl }),
  }));

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      {personSchemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <Breadcrumb items={[{ label: 'Team' }]} />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-body-text">Loading team members...</p>
                </div>
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No team members to display.
              </div>
            ) : (
              <div className="max-w-4xl mx-auto space-y-8">
                {teamMembers.map((member) => (
                  <div key={member.id} id={`${member.id}`} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="md:flex">
                      <div className="md:w-1/3 bg-gradient-to-br from-brand-purple to-brand-green p-8 flex items-center justify-center">
                        {member.avatarUrl ? (
                          <img
                            src={member.avatarUrl}
                            alt={member.fullName}
                            className="w-48 h-48 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-48 h-48 bg-white rounded-full flex items-center justify-center">
                            <span className="text-6xl font-bold text-brand-purple">
                              {member.fullName.split(' ').map((n) => n[0]).join('')}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="md:w-2/3 p-8">
                        <h2 className="text-3xl font-bold text-navy-dark mb-2">{member.fullName}</h2>
                        <p className="text-xl text-brand-purple mb-6">{member.jobTitle}</p>
                        <p className="text-lg text-body-text mb-6 whitespace-pre-line">{member.bio}</p>
                        {member.linkedinUrl && (
                          <div className="flex space-x-4">
                            <a
                              href={member.linkedinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand-purple hover:text-brand-green transition-colors"
                            >
                              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <FooterSection />
      </div>
    </>
  );
}